/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import { inject, Injectable } from '@angular/core';

import {
    auditTime, BehaviorSubject, catchError, filter, finalize, forkJoin, from, map, Observable,
    of, share, Subject, Subscription, switchMap, tap, throwError
} from 'rxjs';
// import { WorkerManager } from 'opfsdb/WorkerManager';

import { AuthService } from '@services/auth.service';
import { ApiService } from '@services/api.service';
import { DbCacheService, DbCacheState } from '@services/db-cache.service';
import { IObjectEvent, StompService } from '@services/stomp.service';
import { StoreService } from '@services/store.service';

import { HttpApiError } from '@models/api/http-api-error';
import { Guid, ObjectOperation, SM } from '@models/base';
import { getHashHex } from '@models/utils/strings';
import {
    CacheCats, CacheState, ICacheArray, ICacheItem, ICacheItemBase, ICacheLink, ICacheStoredItem, InitCacheItem, ObjectType2CacheCat,
    TCacheCatDef, TCacheCats, TCacheCatsAny, TCacheCatType, TCacheStoredCatAnyType
} from './cache.types';
// import { getRandomId } from '@root/_models';

// import { IBasicRecord, ICommandInputs } from 'opfsdb';

// export class WorkerManager {

// 	constructor(
// 		private worker: Worker,
// 		private sharedWorker: SharedWorker,
// 	) {
//         console.warn('[WorkerManager]', 'worker:', worker, 'sharedWorker:', sharedWorker);
//         this.worker.postMessage({ workerPort: this.sharedWorker.port }, [this.sharedWorker.port]);
//         window.addEventListener('beforeunload', () => {
//             this.worker.postMessage({ closing: true });
//         });
//     }

// 	public sendCommand = async <Command extends ICommandInputs<ReturnType>, ReturnType extends IBasicRecord = IBasicRecord>(
// 		command: Command
// 	): Promise<ReturnType | ReturnType[] | string[] | void> => {
// 		const worker = this.worker;
// 		const channel = new MessageChannel();

// 		return new Promise((resolve, reject) => {
// 			channel.port2.onmessage = ({ data }) => {
// 				if (data.error) {
// 					reject(data.error);
// 				} else {
// 					resolve(data.result);
// 					channel.port2.close();
// 				}
// 			}

// 			worker.postMessage({ port: channel.port1, command }, [channel.port1]);
// 		});
// 	}
// }

const DEBUG = false;

const LIMITS_CHECK_INTERVAL = 60;           // regular limits check interval, seconds
const WATCHED_ITEMS_TTL = 5 * 60;           // how long update once watched item after count == 0, seconds
const WATCHED_PROJECT_TTL = 5 * 60;         // how long update once watched project's items after count == 0, seconds
const WATCHED_LIST_TTL = 5 * 60;            // how long update once watched full list of items after count == 0, seconds
const NO_ITEM_ERROR = new HttpApiError('Ошибка получения данных', 'Попробуйте позже.', 0, 'info');

const _w = DEBUG ? Function.prototype.bind.apply(console.warn, [console, '[Cache]']) : () => {};
const _l = DEBUG ? Function.prototype.bind.apply(console.log, [console, '[Cache]']) : () => {};

const FILE_PREVIEW_MAX_STORE_SIZE = 50 * 1024;        // max blob size of image preview to store in DB, bytes

@Injectable({ providedIn: 'root' })
export class CacheService {

    private readonly _state = new BehaviorSubject(CacheState.Unknown);
    readonly state = this._state.asObservable();
    readonly getState = () => this._state.getValue();

    // workerManager!: WorkerManager;

    // private _CID = getRandomId(10);

    private _dbReady: boolean = false;
    private _nextDbInit: number = 0;
    private _clearSubscription: any;
    private _dbSubscription?: Subscription;
    private _short2Id: SM<Guid> = {};

    private readonly _CATS: { [T in TCacheCats]: TCacheCatDef<T> } = {
        _any_: {
            api: {
                get: t => {
                    if (t == 'wfConstraints') {
                        return this._api.getWorkflowParams('constraints').pipe(map(x => ({ id: 'wfConstraints', item: x })));
                    }
                    else if (t == 'wfTriggers') {
                        return this._api.getWorkflowParams('triggers').pipe(map(x => ({ id: 'wfTriggers', item: x })));
                    }
                    else if (t == 'wfActions') {
                        return this._api.getWorkflowActions().pipe(map(x => ({ id: 'wfActions', item: x })));
                    }
                    else if ( t == 'systemTaskTypes') {
                        return this._api.getTaskTypeTemplates().pipe(map(x => ({ id: 'systemTaskTypes', item: x?.map((t, i) => ({ ...t, id: `system_${i}` })) })));
                    }
                    else {
                        return throwError(() => `Unknown temp cache item type: "${t}"`);
                    }
                }
            },
            mem: {
                ttl: t => {
                    if (t == 'wfConstraints' || t == 'wfTriggers' || t == 'wfActions') {
                        return 30 * 60;
                    }
                    else if (t == 'systemTaskTypes') {
                        return 10 * 60;
                    }
                    else {
                        return 10;
                    }
                }
            }
        },
        tag: {
            api: {
                get: id => this._api.getTag(id),
                getArray: ids => forkJoin(ids.map(id => this._api.getTag(id))).pipe(catchError(() => of()), filter(x => !!x)),
            },
            // db: {
            //     get: id => this._db.getTag(id),
            //     getArray: ids => this._db.getTags(ids),
            //     getArray4project: pid => this._db.getTags4project(pid),
            //     put: v => this._db.putTags(v),
            //     remove: v => this._db.removeTags(v),
            //     clear: onlyUntrusted => this._db.clearTags(onlyUntrusted),
            // },
            eventsTrack: true,
            untrusted: true,
            mem: {
                ttl: 10 * 60,
            },
            // _hasFullProj: {},
        },
        file: {
            api: {
                get: id => this._api.getFile(id),
                getArray: ids => this._api.getFiles(ids),
            },
            // db: {
            //     get: id => this._db.getFile(id),
            //     getArray: ids => this._db.getFiles(ids),
            //     put: v => this._db.putFiles(v),
            //     putFilter: v => !(
            //         !v?.id
            //         || !v.uploaded
            //         || !v.mimeType
            //         || (v.mimeType.startsWith('image') && !v.imgView)
            //         || (v.mimeType == 'application/pdf' && (!v.pdfView || !v.imgView))
            //     ),
            //     remove: v => this._db.removeFiles(v),
            //     clear: onlyUntrusted => this._db.clearFiles(onlyUntrusted),
            // },
            eventsTrack: true,
            mem: {
                ttl: 60,
                putFilter: v => !(
                    !v?.id
                    || !v.uploaded
                    || !v.mimeType
                    || (v.mimeType.startsWith('image') && !v.imgView)
                    || (v.mimeType == 'application/pdf' && (!v.pdfView || !v.imgView))
                ),
            }
        },
        preview: {
            api: {
                get: id => this._api.getFileDownloadLink(id).pipe(
                    switchMap(urls => urls?.previewUrl ? this._api.getUrl(urls.previewUrl) : throwError(() => 'There is no preview for that file.')),
                    map(blob => ({ id, blob, size: blob?.size, dt: Date.now() }))
                ),
            },
            db: {
                get: id => this._db.getFilePreview(id),
                getArray: ids => this._db.getFilePreviews(ids),
                put: v => this._db.putFilePreviews(v),
                putFilter: v => !!v && !!v.size && v.size <= FILE_PREVIEW_MAX_STORE_SIZE,
                remove: v => this._db.removeFilePreviews(v),
                clear: onlyUntrusted => this._db.clearFilePreviews(onlyUntrusted),
            }
        },
        taskType: {
            api: {
                get: id => this._api.getTaskTypeDef(id),
                getArray4project: pid => this._api.getTaskTypeDefsForProject(pid),
            },
            // db: {
            //     get: id => this._db.getTaskType(id),
            //     getArray: ids => this._db.getTaskTypes(ids),
            //     getArray4project: pid => this._db.getTaskTypes4project(pid),
            //     put: v => this._db.putTaskTypes(v),
            //     remove: v => this._db.removeTaskTypes(v),
            //     clear: onlyUntrusted => this._db.clearTaskTypes(onlyUntrusted),
            // },
            eventsTrack: true,
            untrusted: true,
            mem: {
                ttl: 5 * 60,
            },
            _hasFullProj: {},
        },
        workflow: {
            api: {
                get: id => this._api.getWorkflow(id),
                getArray: ids => forkJoin(ids.map(id => this._api.getWorkflow(id))).pipe(catchError(() => of()), filter(x => !!x)),
                getArray4project: pid => this._api.getWorkflowsForProject(pid),
            },
            // db: {
            //     get: id => this._db.getWorkflow(id),
            //     getArray: ids => this._db.getWorkflows(ids),
            //     getArray4project: pid => this._db.getWorkflows4project(pid),
            //     put: v => this._db.putWorkflows(v),
            //     remove: v => this._db.removeWorkflows(v),
            //     clear: onlyUntrusted => this._db.clearWorkflows(onlyUntrusted),
            // },
            eventsTrack: true,
            untrusted: true,
            mem: {
                ttl: 5 * 60,
            },
            _hasFullProj: {},
        },
        urlPreview: {
            api: {
                get: id => this._api.getUrlPreviews([id]).pipe(map(x => x[0]), filter(x => !!x)),
                getArray: ids => this._api.getUrlPreviews(ids),
            },
            db: {
                get: id => this._db.getUrlPreview(id),
                getArray: ids => this._db.getUrlPreviews(ids),
                put: v => this._db.putUrlPreviews(v),
                remove: v => this._db.removeUrlPreviews(v),
                clear: onlyUntrusted => this._db.clearUrlPreviews(onlyUntrusted),
            },
            eventsTrack: true,
            untrusted: true,
            mem: {
                ttl: 10 * 60,
            },
        },
        kanban: {
            api: {
                get: id => this._api.getKanbanBoard(id),
                getArray: ids => forkJoin(ids.map(id => this._api.getKanbanBoard(id))).pipe(catchError(() => of()), filter(x => !!x)),
                getFullList: () => this._api.getKanbanBoardsList(),
            },
            eventsTrack: true,
            untrusted: true,
            mem: {
                ttl: 10 * 60,
            },
            _hasFull: false
        },
        task: {
            api: {
                get: id => this._api.getTask(id, this._store.activeOrgId.getValue()),
                getArray: ids => this._api.getTasks(ids),
            },
            eventsTrack: true,
            untrusted: true,
            mem: {
                ttl: 60,
            },
            on: {
                update: tasks => tasks.forEach(t => this._short2Id[t.shortId!] = t.id!),
                remove: ids => ids.forEach(id => delete this._short2Id[id]),
            },
            transformId: id => this._short2Id[id] || id,
        },
        project: {
            api: {
                get: id => this._auth.getProject(id),
                getArray: ids => forkJoin(ids.map(id => this._auth.getProject(id))).pipe(catchError(() => of()), filter(x => !!x)),
                getFullList: () => this._auth.getProjects(),
            },
            eventsTrack: true,
            untrusted: true,
            mem: {
                ttl: 10 * 60,
            },
            _hasFull: false
        },
        tfilter: {
            api: {
                get: id => this._api.getTasksFilter(id),
                getArray: ids => forkJoin(ids.map(id => this._api.getTasksFilter(id))).pipe(catchError(() => of()), filter(x => !!x)),
                getFullList: () => this._api.getTasksFiltersList(),
            },
            eventsTrack: true,
            untrusted: true,
            mem: {
                ttl: 60,
            },
        },
        chat: {
            api: {
                get: id => this._api.getChat(id),
                getArray: ids => forkJoin(ids.map(id => this._api.getChat(id))).pipe(catchError(() => of()), filter(x => !!x)),
                getFullList: () => this._api.getChatsList(),
            },
            eventsTrack: true,
            untrusted: true,
            mem: {
                ttl: 10 * 60,
            },
            _hasFull: false
        },
        userStatus: {
            api: {
                getFullList: () => this._api.getUserStatuses(),
            },
            eventsTrack: true,
            untrusted: true,
            mem: {
                ttl: 10 * 60,
            },
            _hasFull: false
        },
        milestone: {
            api: {
                get: id => this._api.getMilestone(id),
                getArray: ids => forkJoin(ids.map(id => this._api.getMilestone(id))).pipe(catchError(() => of()), filter(x => !!x)),
                getArray4project: pid => this._api.getMilestonesForProject(pid),
            },
            eventsTrack: true,
            untrusted: true,
            mem: {
                ttl: 3 * 60,
            },
            _hasFullProj: {},
        },
    };

    private readonly _cache: { [T in TCacheCats]?: { [id: Guid]: ICacheStoredItem<T> } } = {};

    private readonly _itemLinks: {
        [T in TCacheCats]?: {
            [id: Guid]: {
                [lid: Guid]: TCacheCats
            }
        }
    } = {};
    private readonly _itemWatchers: { [T in TCacheCats]?: { [id: Guid]: { la: number, count: number, subj: BehaviorSubject<ICacheItem<T>> } } } = {};
    private readonly _projectWatchers: { [T in TCacheCats]?: { [pid: Guid]: { la: number, count: number, subj: BehaviorSubject<ICacheArray<T>> } } } = {};
    private readonly _listWatchers: { [T in TCacheCats]?: { la: number, count: number, subj: BehaviorSubject<ICacheArray<T>> } } = {};

    private readonly _db    = inject(DbCacheService);
    private readonly _auth  = inject(AuthService);
    private readonly _api   = inject(ApiService);
    private readonly _stomp = inject(StompService);
    private readonly _store = inject(StoreService);

    constructor() {
        // _w('constructor', this._CID);
        this._state.next(CacheState.Init);
        this.init();
        this._stomp.onObjectEvent.subscribe(e => this._handleObjectEvent(e));
        this._store.syncLost.pipe(auditTime(5000)).subscribe(() => this.invalidate());
        this._store.onActiveOrgChange.pipe(auditTime(200)).subscribe(() => this._notifyAllListWatchers())
        // this.workerManager = new WorkerManager(new Worker(new URL('./opfsdb.worker', import.meta.url)), new SharedWorker(new URL('./opfsdb.shared.worker', import.meta.url)));
    }

    // for debug
    __getCache(): any {
        return { settings: this._CATS, _DATA: this._cache };
    }

    init(): void {
        // _w('init, _db.getState():', this._db.getState());
        this._dbReady = false;
        if (this._db.getState() == DbCacheState.Ready) {
            // _l(`init, clearDb()`);
            this.clearDb().then(() => {
                // _l('init, set _dbReady = true');
                this._dbReady = true;
                this._checkLimits();
            });
        }
        else {
            if (this._dbSubscription && !this._dbSubscription.closed) {
                this._dbSubscription.unsubscribe()
                this._dbSubscription = undefined;
            }
            // _l('init, subscribe to _db.state');
            this._dbSubscription = this._db.state.subscribe(st => {
                // _l('init, db.state:', st);
                if (st == DbCacheState.Ready){
                    // _l('init, DbCacheState.Ready, _dbReady:', this._dbReady);
                    if (!this._dbReady) {
                        // _l(`init, db.state clearDb()`);
                        this.clearDb().then(() => {
                            // _l('init, set _dbReady = true');
                            this._dbReady = true;
                            this._checkLimits();
                        });
                    }
                }
                else {
                    // _l('init, set _dbReady = false');
                    this._dbReady = false;
                }
            });
            setTimeout(() => {  // wait 2 seconds for DB initializing, then mark cache as ready anyway and scheduler db init
                // _l('init, after 2 seconds, getState()', this.getState());
                if (this.getState() != CacheState.Ready) {
                    // _l('init, set state = CacheState.Ready');
                    this._state.next(CacheState.Ready);
                }
                // _l('init, after 2 seconds, _db.getState()', this._db.getState());
                // if (this._db.getState() != DbCacheState.Ready) {
                //     this.initDb(true);
                // }
            }, 2000);
        }
        this.clearMem();
    }

    initDb(now = false): void {
        // _l('initDb, now:', now);
        if ((now || Date.now() > this._nextDbInit) && this._db.getState() > DbCacheState.Blocked && this._db.getState() < DbCacheState.ServiceDestroing) {
            // _l('initDb, calling _db.initDB');
            this._db.initDB();
            this._nextDbInit = Date.now() + (this._nextDbInit > 0 ? 10 * 60 * 1000 : 20 * 1000);
            // _l('initDb, _nextDbInit:', this._nextDbInit);
        }
    }

    clearMem(onlyUntrusted?: boolean): void {
        (Object.keys(this._listWatchers) as TCacheCats[]).forEach(name => {
            const cat = this._CATS[name];
            if (!onlyUntrusted || cat.untrusted) {
                if (cat._hasFull != undefined) {
                    cat._hasFull = false;
                }
                const watcher = this._listWatchers[name];
                if (watcher?.subj || watcher?.subj.closed) {
                    delete this._listWatchers[name];
                }
                else {
                    this.getList(name).subscribe();
                }
            }
        });
        (Object.keys(this._projectWatchers) as TCacheCats[]).forEach(name => {
            const cat = this._CATS[name];
            if (!onlyUntrusted || cat.untrusted) {
                const watchers = this._projectWatchers[name];
                if (cat._hasFullProj) {
                    cat._hasFullProj = {};
                }
                if (watchers) {
                    for (const id of Object.keys(watchers)) {
                        if (watchers[id]?.subj || watchers[id]?.subj.closed) {
                            delete watchers[id];
                        }
                        else {
                            this.get4project(name, id).subscribe();
                        }
                    }
                }
            }
        });
        (Object.keys(this._itemWatchers) as TCacheCats[]).forEach(<T extends TCacheCats>(cat: T) => {
            const watchers: { [id: Guid]: { la: number, count: number, subj: BehaviorSubject<ICacheItem<T>> } } | undefined = this._itemWatchers[cat];
            if (watchers) {
                if (onlyUntrusted) {
                    for (const id of Object.keys(watchers)) {
                        if (!watchers[id]?.subj || watchers[id]?.subj.closed) {
                            delete watchers[id];
                        }
                        else {
                            this.get4project(cat, id).subscribe();
                        }
                    }
                }
                else {
                    Object.values(watchers).forEach(wt => {
                        // _l('clearMem, _itemWatchers, watcher:', wt);
                        if (wt?.subj && !wt.subj.closed) {
                            wt.subj.complete();
                        }
                    });
                    delete this._itemWatchers[cat];
                }
            }
        });
        (Object.keys(this._cache) as TCacheCats[]).forEach(cat => {
            delete this._cache[cat];
        });
        (Object.keys(this._CATS) as TCacheCats[]).forEach(cat => {
            if (this._CATS[cat]._hasFull !== undefined) {
                this._CATS[cat]._hasFull = false;
            }
            if (this._CATS[cat]._hasFullProj) {
                this._CATS[cat]._hasFullProj = {};
            }
        });
        this._short2Id = {};
    }

    async clearDb(): Promise<void> {
        // _l('clearDb, _dbReady:', this._dbReady);
        if (this._dbReady) {
            const list = Object.values(this._CATS).filter(cat => cat.untrusted && cat.db).map(cat => cat.db!.clear());
            // _l('clearDb, clear list:', list);
            await Promise.all(list);
        }
    }

    async invalidate(): Promise<void> {
        // _w('invalidate');
        await this.invalidateDb();
        this.invalidateMem();
    }

    invalidateMem(): void {
        // _l('invalidateMem');
        (Object.keys(this._listWatchers) as TCacheCats[]).forEach(name => {
            const cat = this._CATS[name];
            if (cat.untrusted) {
                if (cat._hasFull != undefined) {
                    cat._hasFull = false;
                }
                const watcher = this._listWatchers[name];
                if (!watcher?.subj || watcher.subj.closed) {
                    delete this._listWatchers[name];
                }
                else {
                    this.getList(name).subscribe();
                }
            }
        });
        (Object.keys(this._projectWatchers) as TCacheCats[]).forEach(name => {
            const cat = this._CATS[name];
            if (cat.untrusted) {
                const watchers = this._projectWatchers[name];
                if (watchers) {
                    // _l('invalidateMem, invalidate projectWatchers:', watchers);
                    for (const id of Object.keys(watchers)) {
                        if (!watchers[id]?.subj || watchers[id].subj.closed) {
                            // _l('invalidateMem, \t remove watcher:', id);
                            delete watchers[id];
                        }
                        else {
                            // _l('invalidateMem, \t refetch watcher data:', id);
                            this.get4project(name, id).subscribe();
                        }
                    }
                }
            }
        });
        (Object.keys(this._itemWatchers) as TCacheCats[]).forEach(name => {
            const cat = this._CATS[name];
            if (cat.untrusted) {
                const watchers = this._itemWatchers[name];
                if (watchers) {
                    for (const id of Object.keys(watchers)) {
                        if (!watchers[id]?.subj || watchers[id]?.subj.closed) {
                            delete watchers[id];
                        }
                        else {
                            this.get(name, id).subscribe();
                        }
                    }
                }
            }
        });
    }

    async invalidateDb(): Promise<void> {
        // _l('invalidateDb, _dbReady:', this._dbReady);
        if (this._dbReady) {
            const list = Object.values(this._CATS).filter(cat => cat.untrusted && cat.db).map(cat => cat.db!.clear(true));
            // _l('invalidateDb, clear list:', list);
            await Promise.all(list);
        }
    }

    eraseAll(): void {
        // TODO: create a single point to clear all user's data
        if (this._clearSubscription) {
            clearInterval(this._clearSubscription);
            this._clearSubscription = undefined;
        }
        (Object.keys(this._cache) as TCacheCats[]).forEach(cat => delete this._cache[cat]);
        this._db.deleteDb();
    }

    put<T extends TCacheCats>(name: T, items: TCacheCatType[T][]): void {
        const cis = items.map(x => ({ id: x.id!, item: x, dt: Date.now(), la: Date.now() }));
        this._storeItems(name, cis);
    }

    get<T extends TCacheCats>(name: T, id: Guid): Observable<ICacheItem<T>> {
        const cat = this._CATS[name];
        // _l('GET', `<${name}> #${id} dbReady:`, this._dbReady, 'cat.db:', !!cat.db, 'cat.api:', !!cat.api);

        if (cat.transformId) {
            id = cat.transformId(id) || id;
            // _l('GET', `<${name}> #${id} transformedId`);
        }

        let si = this._cache[name]?.[id];
        // _l('GET', `\t <${name}> #${id} stored item:`, si);
        const getItemFn = () => {
            if (si) {
                const usi = this._cache[name]?.[id];
                if (usi) {
                    // _l('GET', `\t <${name}> #${id} stored item is present`);
                    usi.la = Date.now();
                    if (usi.apiReq) {
                        // _l('GET', `\t <${name}> #${id} object is already being fetched from api, return(1) apiReq Observable`);
                        return usi.apiReq;
                    }
                    else if (usi.item) {
                        // _l('GET', `\t <${name}> #${id} object exists in mem cache, update la, return item`);
                        return of({ id, item: usi.item, dt: usi.dt });
                    }
                }
            }
            if (cat.db) {
                // _l('GET', `\t <${name}> #${id} cheking for item in DB`);
                return from(cat.db.get(id)).pipe(
                    switchMap(item => {
                        if (item) {
                            const nsi: ICacheStoredItem<T> = { id, dt: Date.now(), la: Date.now(), item };
                            this._storeItemMem(name, id, nsi);
                            // _l('GET', `\t <${name}> #${id} return item from db`);
                            if (si?.getting) {
                                si.getting.next();
                                si.getting.complete();
                                si.getting = undefined;
                            }
                            return of(nsi);
                        }
                        else if (cat.api?.get) {
                            const apiReq = this._getFromApi(name, id, cat.api.get);
                            this._storeItemMem(name, id, { id, dt: Date.now(), la: Date.now(), apiReq, loading: true }, true);
                            // _l('GET', `\t <${name}> #${id} set object as loading, return(2) apiReq Observable`);
                            if (si?.getting) {
                                si.getting.next();
                                si.getting.complete();
                                si.getting = undefined;
                            }
                            return apiReq;
                        }
                        if (si?.getting) {
                            si.getting.next();
                            si.getting.complete();
                            si.getting = undefined;
                        }
                        return of({ id, dt: Date.now(), err: NO_ITEM_ERROR } as ICacheItem<T>);
                    })
                );
            }
            else if (cat.api?.get) {
                // _l('GET', `\t <${name}> #${id} requesting from backend(2), cat.api.get`, cat.api.get);
                const apiReq = this._getFromApi(name, id, cat.api.get);
                this._storeItemMem(name, id, { id, dt: Date.now(), la: Date.now(), apiReq, loading: true }, true);
                // _l('GET', `\t <${name}> #${id} set object as loading, return(3) apiReq Observable`);
                return apiReq;
            }
            return of({ id, dt: Date.now(), err: NO_ITEM_ERROR } as ICacheItem<T>);
        };
        if (si) {
            // _l('GET', `\t <${name}> #${id} stored item is present`);
            si.la = Date.now();
            if (si.apiReq) {
                // _l('GET', `\t <${name}> #${id} object is already being fetched from api, return(1) apiReq Observable`);
                return si.apiReq;
            }
            else if (si.item) {
                // _l('GET', `\t <${name}> #${id} object exists in mem cache, update la, return item`);
                return of({ id, item: si.item, dt: si.dt });
            }
            else if (si.getting) {
                // _l('GET', `\t <${name}> #${id} object is being getting right now, return observable`);
                return si.getting.pipe(
                    switchMap(getItemFn)
                );
            }
            else {
                // _l('GET', `\t <${name}> #${id} no item, no cat.db, no cat.api, return item error:`, si.err);
                return of({ id, dt: si.dt, err: si.err || NO_ITEM_ERROR });
            }
        }
        else if (cat.db || cat.api?.get) {
            // _l('GET', `\t <${name}> #${id} create stored item with getting = true`);
            si = { id, la: Date.now(), dt: Date.now(), getting: new Subject() };
            this._storeItemMem(name, id, si, true);
            return getItemFn();
        }

        // _l('GET', `\t <${name}> #${id} no memTtl, no cat.db, no cat.api, return item error`);
        return of({ id, dt: Date.now(), err: NO_ITEM_ERROR });
    }

    getSync<T extends TCacheCats>(name: T, id: Guid): ICacheItem<T> | undefined {
        return this._cache[name]?.[id];
    }

    getAny<T extends TCacheCatsAny>(tn: T): Observable<ICacheItemBase<TCacheStoredCatAnyType[T]>> {
        return this.get('_any_', tn).pipe(map(x => ({ ...x, item: x.item.item })));
    }

    watch<T extends TCacheCats>(name: T, id: Guid): Observable<ICacheItem<T>> {
        if (name == 'chat') {
            // _w('WATCH', `<${name}|${this._CID}> #${id}`, this._cache[name]?.[id]);
        }
        const cat = this._CATS[name];
        // _l('WATCH', `<${name}> #${id} dbReady:`, this._dbReady, 'cat.db:', !!cat.db, 'cat.api:', !!cat.api);
        if (!this._itemWatchers[name]) {
            this._itemWatchers[name] = {};
        }
        if (!this._itemWatchers[name][id]) {
            this._itemWatchers[name][id] = { count: 0, la: Date.now(), subj: new BehaviorSubject<ICacheItem<T>>({ id, dt: Date.now(), loading: true }) };
            // _l('WATCH', `\t <${name}> #${id}, create new subject`);
        }
        const wi = this._itemWatchers[name][id];
        const obs = new Observable<ICacheItem<T>>(subscriber => {
            const subscription = wi.subj.subscribe(subscriber);
            wi.count++;
            wi.la = Date.now();
            // _l('WATCH', `\t <${name}> #${id}, new subscriber, count: ${wi.count}`);
            return () => {
                subscription.unsubscribe();
                wi.count--;
                wi.la = Date.now();
                // _l('WATCH', `\t <${name}> #${id}, unsubscribe, count: ${wi.count}`);
                if (wi.count == 0) {
                    // _l('WATCH', `\t <${name}> #${id}, delete watcher without subscribers`);
                    delete this._itemWatchers[name]?.[id];
                }
            }
        });
        this.get(name, id).subscribe(item => {
            // _l('WATCH', `\t <${name}> #${id} return item from GET:`, item);
            wi.subj.next(item);
        });
        if (name == 'chat') {
            // _l('WATCH', '\t chats in mem:', Object.values(this._cache.chat!), '\n\t\t A6EB2AE0-94F5-4B8B-8E04-84EE6BFBC974:', this._cache.chat!['A6EB2AE0-94F5-4B8B-8E04-84EE6BFBC974']);
        }
        return obs;
    }

    getArray<T extends TCacheCats>(name: T, ids: Guid[]): Observable<ICacheItem<T>[]> {
        const hid = '___ARR_' + getHashHex(ids.sort().join('|'));
        const cat = this._CATS[name];
        // _l('GETarray', `<${name}>  dbReady:`, this._dbReady, 'cat.db:', !!cat.db, 'cat.api:', !!cat.api);

        let si = this._cache[name]?.[hid];
        // _l('GETarray', `\t <${name}> #${hid} stored item:`, si);
        const sis: SM<ICacheItem<T>> = {};
        ids.forEach(id => {
            const sitem = this._cache[name]?.[id];
            // _l('GETarray', `\t <${name}> #${id} si:`, sitem);
            if (sitem?.item) {
                sitem.la = Date.now();
                sis[id] = sitem;
            }
        });

        if (Object.values(sis).length == ids.length) {
            // _l('GETarray', `\t <${name}> #${hid} all items exist in mem cache, update la, return items`);
            return of(Object.values(sis));
        }

        const getItemsFn = () => {
            if (si) {
                const usi = this._cache[name]?.[hid];
                if (usi) {
                    // _l('GETarray', `\t <${name}> #${hid} getItemsFn, stored ___ARR_item is present:`, usi);
                    if (usi.arrayApiReq) {
                        // _l('GETarray', `\t <${name}> #${hid} getItemsFn, objects is already being fetched from api, return(1) arrayApiReq Observable`);
                        return usi.arrayApiReq;
                    }
                    else {
                        const items: ICacheItem<T>[] = [];
                        ids.forEach(id => {
                            const sitem = this._cache[name]?.[id];
                            if (sitem?.item) {
                                sitem.la = Date.now();
                                items.push(sitem);
                                sis[id] = sitem;
                            }
                        });
                        if (items.length == ids.length) {
                            // _l('GETarray', `\t <${name}> #${hid} getItemsFn, objects exists in mem cache, update la, return item`);
                            return of(items);
                        }
                    }
                }
            }
            if (cat.db) {
                // _l('GETarray', `\t <${name}> #${hid} cheking for items in DB`);
                return from(cat.db.getArray(ids)).pipe(
                    switchMap(items => {
                        // _l('GETarray', '+++++++++++++++++++++++++++++');
                        if (items?.length == ids.length) {
                            const sitems = items.filter(item => !!item).map(item => ({ id: item?.id!, dt: Date.now(), la: Date.now(), item }));
                            sitems.forEach(item => this._storeItemMem(name, item.id!, item));
                            // _l('GETarray', `\t <${name}> #${hid} getItemsFn, return items from db`);
                            if (si?.getting) {
                                si.getting.next();
                                si.getting.complete();
                                si.getting = undefined;
                                delete this._cache[name]?.[hid];
                            }
                            return of(sitems);
                        }
                        else if (cat.api?.getArray) {
                            const arrayApiReq = this._getFromApiArray(name, ids, cat.api.getArray);
                            this._storeItemMem(name, hid, { id: hid, dt: Date.now(), la: Date.now(), arrayApiReq, loading: true }, true);
                            // _l('GETarray', `\t <${name}> #${hid} set objects as loading, return(2) arrayApiReq Observable`);
                            if (si?.getting) {
                                si.getting.next();
                                si.getting.complete();
                                si.getting = undefined;
                                delete this._cache[name]?.[hid];
                            }
                            return arrayApiReq;
                        }
                        // _l('GETarray', `\t <${name}> #${hid} no items in DB, no api.getArray, finish and return empty array`);
                        if (si?.getting) {
                            si.getting.next();
                            si.getting.complete();
                            si.getting = undefined;
                            delete this._cache[name]?.[hid];
                        }
                        return of([]);
                    })
                );
            }
            else if (cat.api?.getArray) {
                // _l('GETarray', `\t <${name}> #${hid} requesting from backend(2), cat.api.getArray`, cat.api.getArray);
                const arrayApiReq = this._getFromApiArray(name, ids, cat.api.getArray);
                this._storeItemMem(name, hid, { id: hid, dt: Date.now(), la: Date.now(), arrayApiReq, loading: true }, true);
                // _l('GETarray', `\t <${name}> #${hid} set objects as loading, return(3) arrayApiReq Observable`);
                return arrayApiReq;
            }
            return of([]);
        };

        if (si) {
            // _l('GETarray', `\t <${name}> #${hid} stored item is present`);
            si.la = Date.now();
            if (si?.arrayApiReq) {
                // _l('GETarray', `\t <${name}> #${hid} object is already being fetched from api, return(1) arrayApiReq Observable`);
                return si.arrayApiReq;
            }
            else if (si.getting) {
                // _l('GETarray', `\t <${name}> #${hid} object is being getting right now, return observable`);
                return si.getting.pipe(
                    switchMap(getItemsFn)
                );
            }
            else {
                // _l('GETarray', `\t <${name}> #${hid} no items, no cat.db, no cat.api.getArray, return empty array`);
                return of([]);
            }
        }
        else if (cat.db || cat.api?.getArray) {
            // _l('GETarray', `\t <${name}> #${hid} create stored item with getting = true`);
            si = { id: hid, la: Date.now(), dt: Date.now(), getting: new Subject() };
            this._storeItemMem(name, hid, si, true);
            return getItemsFn();
        }

        // _l('GETarray', `\t <${name}> #${hid} no memTtl, no cat.db, no cat.api, return empty array`);
        return of([]);
    }

    get4project<T extends TCacheCats>(name: T, pid: Guid): Observable<ICacheArray<T>> {
        const hid = '___PRJ_' + pid;
        const cat = this._CATS[name];
        _l('GET4project', `<${name}> #${hid} dbReady:`, this._dbReady, 'cat._hasFullProj:', cat._hasFullProj?.[pid], 'cat.db:', !!cat.db, 'cat.api:', !!cat.api, 'mem.ttl:', cat.mem?.ttl);

        let si = this._cache[name]?.[hid];
        _l('GET4project', `\t <${name}> #${hid} stored item:`, si);

        const getItemsFn = () => {
            if (si) {
                const usi = this._cache[name]?.[hid];
                if (usi) {
                    _l('GET4project', `\t <${name}> #${hid} getItemsFn, stored ___PRJ_item is present`);
                    if (usi.projectApiReq) {
                        _l('GET4project', `\t <${name}> #${hid} getItemsFn, objects is already being fetched from api, return(1) projectApiReq Observable`);
                        return usi.projectApiReq;
                    }
                    else if (cat.mem && cat._hasFullProj?.[pid]) {
                        _l('GET4project', `\t <${name}> #${hid} getItemsFn, objects exists in mem cache (_hasFullProj), update la, return item`);
                        const items = Object.values<ICacheStoredItem<T>>(this._cache[name] || {}).filter(x => (x?.item as any)?.projectId == pid);
                        items.forEach(x => x.la = Date.now());
                        return of({ la: Date.now(), dt: Date.now(), items: items.map(x => x.item) } as ICacheArray<T>);
                    }
                }
            }
            if (cat.db?.getArray4project && cat._hasFullProj?.[pid]) {
                _l('GET4project', `\t <${name}> #${hid} cheking for items in DB (_hasFullProj)`);
                return from(cat.db.getArray4project(pid)).pipe(
                    switchMap(items => {
                        if (items?.length) {
                            const sitems = items.filter(item => !!item).map(item => ({ id: item?.id!, dt: Date.now(), la: Date.now(), item }));
                            sitems.forEach(item => this._storeItemMem(name, item.id!, item));
                            _l('GET4project', `\t <${name}> #${hid} getItemsFn, return items from db`);
                            if (si?.getting) {
                                si.getting.next();
                                si.getting.complete();
                                si.getting = undefined;
                            }
                            return of({ la: Date.now(), dt: Date.now(), items: sitems.map(x => x.item) });
                        }
                        else if (cat.api?.getArray4project) {
                            const projectApiReq = this._getFromApiArray4project(name, pid, cat.api.getArray4project).pipe(
                                switchMap(items => of({ dt: Date.now(), items: items?.map(x => x.item) } as ICacheArray<T>)),
                                finalize(() => this._removeItemsMem(name, [hid]))
                            );
                            this._storeItemMem(name, hid, { id: hid, dt: Date.now(), la: Date.now(), projectApiReq, loading: true }, true);
                            _l('GET4project', `\t <${name}> #${hid} set objects as loading, return(2) projectApiReq Observable`);
                            if (si?.getting) {
                                si.getting.next();
                                si.getting.complete();
                                si.getting = undefined;
                            }
                            return projectApiReq;
                        }
                        if (si?.getting) {
                            si.getting.next();
                            si.getting.complete();
                            si.getting = undefined;
                        }
                        return of({ dt: Date.now(), err: NO_ITEM_ERROR });
                    })
                );
            }
            else if (cat.api?.getArray4project) {
                _l('GET4project', `\t <${name}> #${hid} requesting from backend(2), cat.api.getArray4project`, cat.api.getArray4project);
                const projectApiReq = this._getFromApiArray4project(name, pid, cat.api.getArray4project).pipe(
                    switchMap(items => of({ dt: Date.now(), items: items?.map(x => x.item) } as ICacheArray<T>)),
                    finalize(() => this._removeItemsMem(name, [hid]))
                );
                this._storeItemMem(name, hid, { id: hid, dt: Date.now(), la: Date.now(), projectApiReq, loading: true }, true);
                _l('GET4project', `\t <${name}> #${hid} set objects as loading, return(3) projectApiReq Observable`);
                return projectApiReq;
            }
            return of({ dt: Date.now(), err: NO_ITEM_ERROR });
        };

        if (si) {
            _l('GET4project', `\t <${name}> #${hid} stored item is present`);
            if (si?.projectApiReq) {
                _l('GET4project', `\t <${name}> #${hid} object is already being fetched from api, return(1) projectApiReq Observable`);
                return si.projectApiReq;
            }
            else if (si.getting) {
                _l('GET4project', `\t <${name}> #${hid} object is being getting right now, return observable`);
                return si.getting.pipe(
                    switchMap(getItemsFn)
                );
            }
            else {
                _l('GET4project', `\t <${name}> #${hid} no items, no cat.db, no cat.api.getArray4project, return error`);
                delete this._cache[name]?.[hid];
                return of({ dt: Date.now(), err: NO_ITEM_ERROR });
            }
        }
        else if (cat.mem && cat._hasFullProj?.[pid]) {
            const items = Object.values<ICacheStoredItem<T>>(this._cache[name] || {}).filter(x => (x?.item as any)?.projectId == pid);
            items.forEach(x => x.la = Date.now());
            _l('GET4project', `\t <${name}> #${hid} all items for project already in cache, returning list:`, items);
            return of({ la: Date.now(), dt: Date.now(), items: items.map(x => x.item) } as ICacheArray<T>);
        }
        else if (cat.db || cat.api?.getArray4project) {
            _l('GET4project', `\t <${name}> #${hid} create stored item with getting = true`);
            si = { id: hid, la: Date.now(), dt: Date.now(), getting: new Subject() };
            this._storeItemMem(name, hid, si, true);
            return getItemsFn();
        }

        _l('GET4project', `\t <${name}> #${hid} no memTtl, no cat.db, no cat.api, return error`);
        return of({ dt: Date.now(), err: NO_ITEM_ERROR });
    }

    watch4project<T extends TCacheCats>(name: T, pid: Guid): Observable<ICacheArray<T>> {
        const hid = '___PRJ_' + pid;
        _l('WATCH4project', `<${name}> #${hid} dbReady:`, this._dbReady);

        if (!this._projectWatchers[name]) {
            this._projectWatchers[name] = {};
        }
        if (!this._projectWatchers[name][pid]) {
            this._projectWatchers[name][pid] = { count: 0, la: Date.now(), subj: new BehaviorSubject<ICacheArray<T>>({ dt: Date.now(), loading: true }) };
        }
        const wi = this._projectWatchers[name][pid];
        const obs = new Observable<ICacheArray<T>>(subscriber => {
            const subscription = wi.subj.subscribe(subscriber);
            wi.count++;
            wi.la = Date.now();
            _l('WATCH4project', `\t <${name}> #${pid}, new subscriber, count: ${wi.count}`);
            return () => {
                subscription.unsubscribe();
                wi.count--;
                wi.la = Date.now();
                _l('WATCH4project', `\t <${name}> #${pid}, delete watcher without subscribers`);
                if (wi.count == 0) {
                    _l('WATCH4project', `\t <${name}> #${pid}, delete project watcher without subscribers`);
                    delete this._projectWatchers[name]?.[pid];
                }
            }
        });
        this.get4project(name, pid).subscribe({
            next: item => {
                _l('WATCH4project', `\t <${name}> #${pid} return item from GET:`, item);
                if (item.items) {
                    wi.subj.next(item);
                }
            },
            error: err => {
                _l('WATCH4project', `\t <${name}> #${pid} error receiving item from GET:`, err);
                wi.subj.next({ dt: Date.now(), err });
            }
        });
        return obs;

    }

    getList<T extends TCacheCats>(name: T): Observable<ICacheArray<T>> {
        const hid = '___ALL_' + name;
        const cat = this._CATS[name];
        // _l('getList', `<${name}> #${hid} dbReady:`, this._dbReady, 'cat._hasFull:', cat._hasFull, 'cat.db:', !!cat.db, 'cat.api:', !!cat.api);

        let si = this._cache[name]?.[hid];
        // _l('getList', `\t <${name}> #${hid} stored item:`, si);

        const getItemsFn = () => {
            if (si) {
                const usi = this._cache[name]?.[hid];
                if (usi) {
                    // _l('getList', `\t <${name}> #${hid} getItemsFn, stored ___ALL_item is present`);
                    if (usi.listApiReq) {
                        // _l('getList', `\t <${name}> #${hid} getItemsFn, objects is already being fetched from api, return(1) listApiReq Observable`);
                        return usi.listApiReq;
                    }
                    else if (cat.mem && cat._hasFull) {
                        // _l('getList', `\t <${name}> #${hid} getItemsFn, objects exists in mem cache (_hasFull), update la, return item`);
                        const items = Object.values<ICacheStoredItem<T>>(this._cache[name] || {}).filter(x => !!x?.id && !x.id.startsWith('___'));
                        items.forEach(x => x.la = Date.now());
                        return of({ la: Date.now(), dt: Date.now(), items: items.map(x => x.item) } as ICacheArray<T>);
                    }
                }
            }
            if (cat.db?.getFullList && cat._hasFull) {
                // _l('getList', `\t <${name}> #${hid} cheking for items in DB (_hasFull)`);
                return from(cat.db.getFullList()).pipe(
                    switchMap(items => {
                        if (items?.length) {
                            const sitems = items.filter(item => !!item).map(item => ({ id: item?.id!, dt: Date.now(), la: Date.now(), item }));
                            sitems.forEach(item => this._storeItemMem(name, item.id!, item));
                            // _l('getList', `\t <${name}> #${hid} getItemsFn, return items from db`);
                            if (si?.getting) {
                                si.getting.next();
                                si.getting.complete();
                                si.getting = undefined;
                            }
                            return of({ la: Date.now(), dt: Date.now(), items: sitems.map(x => x.item) });
                        }
                        else if (cat.api?.getFullList) {
                            const listApiReq = this._getFromApiFullList(name, cat.api.getFullList).pipe(
                                switchMap(items => of({ dt: Date.now(), items: items?.map(x => x.item) } as ICacheArray<T>)),
                                finalize(() => this._removeItemsMem(name, [hid]))
                            );
                            this._storeItemMem(name, hid, { id: hid, dt: Date.now(), la: Date.now(), listApiReq, loading: true }, true);
                            // _l('getList', `\t <${name}> #${hid} set objects as loading, return(2) listApiReq Observable`);
                            if (si?.getting) {
                                si.getting.next();
                                si.getting.complete();
                                si.getting = undefined;
                            }
                            return listApiReq;
                        }
                        if (si?.getting) {
                            si.getting.next();
                            si.getting.complete();
                            si.getting = undefined;
                        }
                        return of({ dt: Date.now(), err: NO_ITEM_ERROR });
                    })
                );
            }
            else if (cat.api?.getFullList) {
                // _l('getList', `\t <${name}> #${hid} requesting from backend(2), cat.api.getFullList`, cat.api.getFullList);
                const listApiReq = this._getFromApiFullList(name, cat.api.getFullList).pipe(
                    switchMap(items => of({ dt: Date.now(), items: items?.map(x => x.item) } as ICacheArray<T>)),
                    finalize(() => this._removeItemsMem(name, [hid]))
                );
                this._storeItemMem(name, hid, { id: hid, dt: Date.now(), la: Date.now(), listApiReq, loading: true }, true);
                // _l('getList', `\t <${name}> #${hid} set objects as loading, return(3) listApiReq Observable`);
                return listApiReq;
            }
            return of({ dt: Date.now(), err: NO_ITEM_ERROR });
        };

        if (si) {
            // _l('getList', `\t <${name}> #${hid} stored item is present`);
            if (si?.listApiReq) {
                // _l('getList', `\t <${name}> #${hid} object is already being fetched from api, return(1) listApiReq Observable`);
                return si.listApiReq;
            }
            else if (si.getting) {
                // _l('getList', `\t <${name}> #${hid} object is being getting right now, return observable`);
                return si.getting.pipe(
                    switchMap(getItemsFn)
                );
            }
            else {
                // _l('getList', `\t <${name}> #${hid} no items, no cat.db, no cat.api.getFullList, return error`);
                delete this._cache[name]?.[hid];
                return of({ dt: Date.now(), err: NO_ITEM_ERROR });
            }
        }
        else if (cat.mem && cat._hasFull) {
            const items = Object.values<ICacheStoredItem<T>>(this._cache[name] || {}).filter(x => !!x?.id && !x.id.startsWith('___'));
            items.forEach(x => x.la = Date.now());
            return of({ la: Date.now(), dt: Date.now(), items: items.map(x => x.item) } as ICacheArray<T>);
        }
        else if (cat.db || cat.api?.getFullList) {
            // _l('getList', `\t <${name}> #${hid} create stored item with getting = true`);
            si = { id: hid, la: Date.now(), dt: Date.now(), getting: new Subject() };
            this._storeItemMem(name, hid, si, true);
            return getItemsFn();
        }

        // _l('getList', `\t <${name}> #${hid} no memTtl, no cat.db, no cat.api, return error`);
        return of({ dt: Date.now(), err: NO_ITEM_ERROR });
    }

    getListSync<T extends TCacheCats>(name: T): ICacheArray<T> {
        const items = Object.values<ICacheStoredItem<T>>(this._cache[name] || {}).filter(x => !!x?.id && !x.id.startsWith('___'));
        items.forEach(x => x.la = Date.now());
        return { la: Date.now(), dt: Date.now(), items: items.map(x => x.item) } as ICacheArray<T>;
    }

    watchList<T extends TCacheCats>(name: T): Observable<ICacheArray<T>> {
        // _l('watchList', `<${name}|${this._CID}> dbReady:`, this._dbReady);

        if (!this._listWatchers[name]) {
            this._listWatchers[name] = { count: 0, la: Date.now(), subj: new BehaviorSubject<ICacheArray<T>>({ dt: Date.now(), loading: true }) };
        }
        const wi = this._listWatchers[name]!;
        const obs = new Observable<ICacheArray<T>>(subscriber => {
            const subscription = wi.subj.subscribe(subscriber);
            wi.count++;
            wi.la = Date.now();
            // _l('watchList', `\t <${name}> new subscriber, count: ${wi.count}`);
            return () => {
                subscription.unsubscribe();
                wi.count--;
                wi.la = Date.now();
                // _l('watchList', `\t <${name}> delete watcher without subscribers`);
                if (wi.count == 0) {
                    // _l('watchList', `\t <${name}>, delete list watcher without subscribers`);
                    delete this._listWatchers[name];
                }
            }
        });
        this.getList(name).subscribe({
            next: item => {
                _l('watchList', `\t <${name}> return item from getList:`, item);
                if (item.items) {
                    wi.subj.next(item);
                }
            },
            error: err => {
                _l('watchList', `\t <${name}> error receiving item from getList:`, err);
                wi.subj.next({ dt: Date.now(), err });
            }
        });
        return obs;
    }

    private _handleObjectEvent(e: IObjectEvent): void {
        // _l('_handleObjectEvent', e, 'ready:', this._dbReady);
        if (!e || !e.type || !this._dbReady) {
            return;
        }
        const name = ObjectType2CacheCat[e.type];
        const cat = this._CATS[name];
        // _l('_handleObjectEvent', '\t name:', name, 'cat:', cat);
        if (!cat || !cat.eventsTrack) {
            return;
        }
        // _l('_handleObjectEvent', '\t operation:', e.operation);
        if (e.operation == ObjectOperation.Create || e.operation == ObjectOperation.Update || e.operation == ObjectOperation.Upload || e.operation == ObjectOperation.Preview) {
            // _l('_handleObjectEvent', 'cat.db:', !!cat.db);
            if (e.objects?.length) {
                const cis: ICacheStoredItem<typeof name>[] = e.objects.map(x => InitCacheItem[name](x)).map(x => ({ id: x?.id!, item: x, dt: Date.now(), la: Date.now() }));
                this._storeItems(name, cis);
                for (const ci of cis) {
                    const id = ci.id;
                    if (id && this._itemLinks[name]) {
                        for (const lid of Object.keys(this._itemLinks[name])) {
                            if (this._itemLinks[name][lid]?.[id]) {
                                // _l('_handleObjectEvent', `get linked object (${id}) -> ${name}#${lid}`);
                                this.get(name, lid);
                            }
                        }
                    }
                }
            }
            else if (e.ids?.length) {
                // _l('_handleObjectEvent', e, 'e.ids:', e.ids);
                if (cat.api?.getArray) {
                    this._getFromApiArray(name, e.ids, cat.api.getArray).subscribe({
                        error: err => { } // TODO
                    });
                }
                else {
                    // TODO
                }
            }
        }
        else if (e.operation == ObjectOperation.Delete) {
            if (cat && e.ids?.length) {
                this._removeItems(name, e.ids.map(id => ({ id })));
            }
        }
    }

    private _notifyItemWatchers<T extends TCacheCats>(name: T, items: ICacheItem<T>[]): void {
        if (this._itemWatchers[name]) {
            for (const ci of items) {
                if (ci.item?.id) {
                    const wi = this._itemWatchers[name][ci.item.id];
                    if (wi && wi.count > 0) {
                        wi.subj.next(ci);
                    }
                }
            }
        }
    }

    private _notifyDeleteItemWatchers<T extends TCacheCats>(name: T, ids: Guid[]): void {
        if (this._itemWatchers[name]) {
            for (const id of ids) {
                const wi = this._itemWatchers[name][id];
                if (wi && wi.count > 0) {
                    wi.subj.next({ id, deleted: true, dt: Date.now() });
                }
            }
        }
    }

    private _notifyProjectWatchers<T extends TCacheCats>(name: T, pids: Guid[]): void {
        const cat = this._CATS[name];
        if (this._projectWatchers[name] && cat) {
            for (const id of pids) {
                const wp = this._projectWatchers[name][id];
                if (wp && wp.count) {
                    if (cat._hasFullProj) {
                        // _l(`_notifyProjectWatchers _cache[${name}]:`, this._cache[name]);
                        const items = Object.values<ICacheStoredItem<T>>(this._cache[name] || {}).filter(x => (x?.item as any)?.projectId == id);
                        items.forEach(x => x.la = Date.now());
                        wp.subj.next({ dt: Date.now(), items: items.map(x => x.item) } as ICacheArray<T>);
                    }
                    else {
                        this.get4project(name, id).subscribe({
                            error: () => delete cat._hasFullProj
                        });
                    }
                }
            }
        }
    }

    private _notifyAllListWatchers(): void {
        (Object.keys(this._cache) as TCacheCats[]).forEach(cat => this._notifyListWatchers(cat));
    }

    private _notifyListWatchers<T extends TCacheCats>(name: T): void {
        const cat = this._CATS[name];
        if (this._listWatchers[name] && cat) {
            const wl = this._listWatchers[name];
            if (wl && wl.count) {
                if (cat._hasFull) {
                    // _l(`_notifyListWatchers<${name}> hasFull _cache[${name}]:`, Object.values(this._cache[name]!));
                    const items = Object.values<ICacheStoredItem<T>>(this._cache[name] || {}).filter(x => !!x?.id && !x.id.startsWith('___'));
                    // _l(`_notifyListWatchers<${name}> \t filtered items:`, items);
                    items.forEach(x => x.la = Date.now());
                    wl.subj.next({ dt: Date.now(), items: items.map(x => x.item) } as ICacheArray<T>);
                }
                else {
                    // _l(`_notifyListWatchers<${name}> NO hasFull getting list`);
                    this.getList(name).subscribe({
                        error: () => delete cat._hasFull
                    });
                }
            }
        }
    }

    private _storeItemMem<T extends TCacheCats>(name: T, id: Guid, si: ICacheStoredItem<T>, force = false): void {
        const cat = this._CATS[name];
        if (cat) {
            if (cat.mem || force) {
                if (!this._cache[name]) {
                    this._cache[name] = {};
                }
                // _l('_storeItem', `\t <${name}> #${id} put item to mem cache:`, si);
                this._cache[name][id] = si;
            }
            else if (this._cache[name]) {
                delete this._cache[name][id];
            }
        }
    }

    private _storeItems<T extends TCacheCats>(name: T, cis: ICacheStoredItem<T>[]): void {
        const cat = this._CATS[name];
        // _l('_storeItems', `<${name}> cat`, cat);
        if (cat && (cat.mem || cat.db)) {
            // _l('_storeItems', `\t <${name}> put items to mem/db cache:`, cis);
            const pids = Object.keys(cis.map(x => (x.item as any)?.projectId).filter(x => !!x).reduce((obj, id) => ({ ...obj, [id]: true }), {}));
            this._storeItemsMem(name, cis);
            if (cat.mem) {
                // _l('_storeItems', `\t <${name}> _notifyItemWatchers, _notifyProjectWatchers`);
                cat.on?.update?.(cis.map(x => (x.item)));
                this._notifyItemWatchers(name, cis);
                this._notifyProjectWatchers(name, pids);
                this._notifyListWatchers(name);
            }
            this._storeItemsDb(name, cis.map(x => x.item).filter(x => !!x)).then((items: any[] | null) => {
                // _l('_storeItems', `\t <${name}> _storeItemsDb.then items:`, items);
                if (items == null) {
                    if (cat._hasFullProj) {
                        // _l('_storeItems', `\t <${name}> _storeItemsDb.then delete _hasFullProj:`, pids);
                        pids.forEach(id => delete cat._hasFullProj![id]);
                    }
                }
                if (!cat.mem) {
                    // _l('_storeItems', `\t <${name}> (2) _notifyItemWatchers, _notifyProjectWatchers`);
                    cat.on?.update?.(cis.map(x => (x.item)));
                    this._notifyItemWatchers(name, cis);
                    this._notifyProjectWatchers(name, pids);
                    this._notifyListWatchers(name);
                }
            });
        }
    }

    private _storeItemsMem<T extends TCacheCats>(name: T, items: ICacheStoredItem<T>[], force = false): void {
        const cat = this._CATS[name];
        if (cat && items?.length) {
            if (cat.mem || force) {
                if (!this._cache[name]) {
                    this._cache[name] = {};
                }
                // _l('_storeItemsMem', `\t <${name}> put items to mem cache:`, items.length == 1 ? items[0] : items);
                for (const si of items) {
                    const siid = si.item?.id;
                    if (siid && si.item && (!cat.mem?.putFilter || cat.mem.putFilter(si.item))) {
                        this._cache[name][siid] = si;
                        // _l('_storeItemsMem', `\t <${name}> _cache[${name}][${siid}] =`, this._cache[name][siid] = si);
                        if (cat.linkedTo) {
                            const links = cat.linkedTo(si.item);
                            if (links?.length) {
                                this._setLinksMem(name, siid, links);
                            }
                        }
                        // _l('_storeItemsMem', `\t <${name}> _cache[${name}] items:`, Object.values(this._cache[name]));
                    }
                }
            }
            else if (this._cache[name]) {
                for (const si of items) {
                    const siid = si.item?.id;
                    if (siid) {
                        // _l('_storeItemsMem', `\t <${name}> remove item from mem cache #${siid}:`, this._cache[name][siid]);
                        delete this._cache[name][siid];
                        if (cat.linkedTo) {
                            this._removeLinksMem(name, siid);
                        }
                    }
                }
            }
        }
    }

    private async _storeItemsDb<T extends TCacheCats>(name: T, items: TCacheCatType[T][]): Promise<TCacheCatType[T][] | null> {
        const cat = this._CATS[name];
        // _l('_storeItemsDb', `\t <${name}> cat.db:`, !!cat.db, 'items:', items?.length);
        if (cat && cat.db && items?.length) {
            const dbItems: any[] = cat.db?.putFilter ? items.filter(x => cat.db!.putFilter!(x)) : items;
            if (dbItems.length) {
                try {
                    // _l('_storeItemsDb', `\t <${name}> put items to DB:`, dbItems);
                    await cat.db.put(dbItems);
                    return Promise.resolve(dbItems);
                }
                catch {
                    return Promise.resolve(null);
                }
            }
        }
        return Promise.resolve([]);
    }

    private _removeItems<T extends TCacheCats>(name: T, items: { id: Guid, projectId?: Guid }[]): void {
        const cat = this._CATS[name];
        if (cat && (cat.mem || cat.db)) {
            const pids = Object.keys(items.map(x => x.projectId).filter(x => !!x).reduce((obj, id) => ({ ...obj, [id!]: true }), {}));
            const ids = items.map(x => x.id);
            this._removeItemsMem(name, ids);
            if (cat.mem) {
                cat.on?.remove?.(ids);
                this._notifyDeleteItemWatchers(name, ids);
                this._notifyProjectWatchers(name, pids);
                this._notifyListWatchers(name);
            }
            this._removeItemsDb(name, ids).then(() => {
                if (!cat.mem) {
                    this._notifyDeleteItemWatchers(name, ids);
                    this._notifyProjectWatchers(name, pids);
                    this._notifyListWatchers(name);
                }
            });
        }
    }

    private _removeItemsMem<T extends TCacheCats>(name: T, ids: Guid[]): void {
        if (!this._cache[name]) {
            return;
        }
        const cat = this._CATS[name];
        if (cat && cat.mem && ids?.length) {
            for (const id of ids) {
                delete this._cache[name][id];
                if (cat.linkedTo) {
                    this._removeLinksMem(name, id);
                }
            }
        }
    }

    private async _removeItemsDb<T extends TCacheCats>(name: T, ids: Guid[]): Promise<void> {
        const cat = this._CATS[name];
        if (cat && cat.db && ids?.length) {
            return cat.db.remove(ids).catch();
        }
        return Promise.resolve();
    }

    private _getFromApi<T extends TCacheCats>(name: T, id: Guid, apiGet: (id: Guid) => Observable<TCacheCatType[T] | undefined>): Observable<ICacheStoredItem<T>> {
        return apiGet(id).pipe(
            switchMap(item => of({ id: item?.id, item, dt: Date.now(), la: Date.now() } as ICacheStoredItem<T>)),
            catchError(err => {
                // _l('_getFromApi', `\t <${name}> #${id} error getting item:`, err);
                return of({ err, dt: Date.now(), la: Date.now() } as ICacheStoredItem<T>);
            }),
            tap({
                next: si => {
                    // _l('_getFromApi', `\t <${name}> #${id} store item`);
                    this._storeItems(name, [si]);
                }
            }),
            share({ resetOnComplete: true, resetOnError: true, resetOnRefCountZero: true })
        );
    }

    private _getFromApiArray<T extends TCacheCats>(name: T, ids: Guid[], apiGetArray: (ids: Guid[]) => Observable<TCacheCatType[T][] | undefined>): Observable<ICacheStoredItem<T>[]> {
        return apiGetArray(ids).pipe(
            switchMap(items => of((items || []).map(item => ({ id: item?.id!, item, dt: Date.now(), la: Date.now() } as ICacheStoredItem<T>)))),
            tap({
                next: sitems => {
                    // _l('_getFromApiArray', `\t <${name}> put items to cache`);
                    this._storeItems(name, sitems);
                }
            }),
            share({ resetOnComplete: true, resetOnError: true, resetOnRefCountZero: true })
        );
    }

    private _getFromApiArray4project<T extends TCacheCats>(name: T, pid: Guid, apiGetArray4project: (pid: Guid) => Observable<TCacheCatType[T][] | undefined>): Observable<ICacheStoredItem<T>[]> {
        return apiGetArray4project(pid).pipe(
            switchMap(items => of((items || []).map(item => ({ id: item?.id!, item, dt: Date.now(), la: Date.now() } as ICacheStoredItem<T>)))),
            tap({
                next: sitems => {
                    // _l('_getFromApiArray4project', `\t <${name}> put items4project to cache`, sitems);
                    const cat = this._CATS[name];
                    if (cat._hasFullProj) {
                        cat._hasFullProj[pid] = true;
                    }
                    this._storeItems(name, sitems);
                }
            }),
            share({ resetOnComplete: true, resetOnError: true, resetOnRefCountZero: true })
        );
    }

    private _getFromApiFullList<T extends TCacheCats>(name: T, apiGetFullList: () => Observable<TCacheCatType[T][] | undefined>): Observable<ICacheStoredItem<T>[]> {
        return apiGetFullList().pipe(
            switchMap(items => of((items || []).map(item => ({ id: item?.id!, item, dt: Date.now(), la: Date.now() } as ICacheStoredItem<T>)))),
            tap({
                next: sitems => {
                    // _l('_getFromApiArray4project', `\t <${name}> put items4project to cache`, sitems);
                    const cat = this._CATS[name];
                    if (cat._hasFull != undefined) {
                        cat._hasFull = true;
                    }
                    this._storeItems(name, sitems);
                }
            }),
            share({ resetOnComplete: true, resetOnError: true, resetOnRefCountZero: true })
        );
    }

    private _checkLimits() {
        // _l('_checkLimits', '_dbReady:', this._dbReady, '_cache:', this._cache);
        if (this._store.isLoggedIn()) {
            if (!this._dbReady) {
                this.initDb();
            }
            for (const name of CacheCats) {
                const now = Date.now();
                const cat = this._CATS[name];
                // _l(`_checkLimits ${name.toLocaleUpperCase()}:`, cat);

                if (this._listWatchers[name] && cat) {
                    // _l(`_checkLimits \t has listWatchers`);
                    const wl = this._listWatchers[name];
                    if (wl && wl.count < 1 && wl.la + WATCHED_LIST_TTL * 1000 - now < 0) {
                        // _l(`_checkLimits \t\t listWatcher expired, deleting`);
                        if (!wl.subj.closed) {
                            wl.subj.complete();
                        }
                        delete this._listWatchers[name];
                    }
                }
                // _l(`_checkLimits \t listWatchers:`, this._listWatchers[name]);

                if (this._projectWatchers[name] && cat) {
                    // _l(`_checkLimits \t has projectWatchers:`, Object.keys(this._projectWatchers[name]).length);
                    for (const id of Object.keys(this._projectWatchers[name])) {
                        const wp = this._projectWatchers[name][id];
                        if (wp && wp.count < 1 && wp.la + WATCHED_PROJECT_TTL * 1000 - now < 0) {
                            // _l(`_checkLimits \t\t projectWatcher[${id}] expired, deleting`);
                            if (!wp.subj.closed) {
                                wp.subj.complete();
                            }
                            delete this._projectWatchers[name][id];
                        }
                    }
                }
                // _l(`_checkLimits \t projectWatchers:`, this._projectWatchers[name]);

                if (this._itemWatchers[name] && cat) {
                    // _l(`_checkLimits \t has itemWatchers:`, Object.keys(this._itemWatchers[name]).length);
                    for (const id of Object.keys(this._itemWatchers[name])) {
                        const wi = this._itemWatchers[name][id];
                        if (wi && wi.count < 1 && wi.la + WATCHED_ITEMS_TTL * 1000 - now < 0) {
                            // _l(`_checkLimits \t\t itemWatcher[${id}] expired, deleting`);
                            if (!wi.subj.closed) {
                                wi.subj.complete();
                            }
                            delete this._itemWatchers[name][id];
                        }
                    }
                }
                // _l(`_checkLimits \t itemWatchers:`, this._itemWatchers[name]);

                // _l(`_checkLimits \t _cache:`, this._cache[name]);
                if (cat.mem) {
                    const ids = Object.keys(this._cache[name] || {});
                    // _l(`_checkLimits \t\t now:`, now);
                    // _l(`_checkLimits \t\t ids:`, { ids });
                    // _l(`_checkLimits \t\t cache/watchers:`, { cat, cache: this._cache[name], iW: this._itemWatchers[name], pW: this._projectWatchers[name], lW: this._listWatchers[name] });
                    if (!this._listWatchers[name]) {
                        for (const id of ids) {
                            const si = this._cache[name]?.[id];
                            const pid = (si?.item as any)?.projectId;
                            const ttl = typeof cat.mem.ttl == 'function' ? cat.mem.ttl(id) : cat.mem.ttl;
                            const expired = si ? si.la + ttl * 1000 - now : 0;
                            // _l(`_checkLimits \t\t si.la:`, si?.la, 'ttl:', ttl, 'now:', now);
                            if (expired < 0 && !this._itemWatchers[name]?.[id] && !this._projectWatchers[name]?.[pid] && !this._listWatchers[name]) {
                                // _l(`_checkLimits \t\t expired:`, expired, this._cache[name]?.[id]);
                                delete this._cache[name]?.[id];
                                if (cat._hasFullProj) {
                                    // _l(`_checkLimits \t\t\t set hasFullProj[${pid}] = false`);
                                    delete cat._hasFullProj[pid];
                                }
                                if (cat._hasFull) {
                                    // _l(`_checkLimits \t\t\t set hasFull = false`);
                                    cat._hasFull = false;
                                }
                                // _l(`_checkLimits \t\t\t DELETE EXPIRED ${name}#${id}`);
                            }
                        }
                    }
                }
            }
        }
        this._setupLimitsCheck();
    }

    private _setupLimitsCheck(): void {
        if (this._clearSubscription) {
            clearInterval(this._clearSubscription);
            this._clearSubscription = undefined;
        }
        // _l(`_setupLimitsCheck, next check in: ${LIMITS_CHECK_INTERVAL} seconds:`, new Date(Date.now() + LIMITS_CHECK_INTERVAL * 1000));
        this._clearSubscription = setTimeout(() => this._checkLimits(), LIMITS_CHECK_INTERVAL * 1000);
    }

    private _setLinksMem<T extends TCacheCats>(name: T, id: Guid, items: ICacheLink[]): void {
        if (!this._itemLinks[name]) {
            this._itemLinks[name] = {};
        }
        this._itemLinks[name][id] = {};
        items.forEach(x => this._itemLinks[name]![id][x.id] = x.t);
    }

    private _removeLinksMem<T extends TCacheCats>(name: T, id: Guid): void {
        if (this._itemLinks[name] && this._itemLinks[name][id]) {
            this._itemLinks[name][id] = {};
        }
    }

}
